import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { useGetFacilitiesQuery, useGetFacilityByIdQuery } from '@/services/facilityApis'
import { useGetAllProductsQuery } from '@/services/productApi'
import { FacilityAddOnType } from '@/types/enums'
import { cn } from '@/lib/utils'
import { formatToPKR } from '@/lib/helper'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { FacilitySchedule } from '@/types/facility/schedule'
import { useGetPriceMastersQuery } from '@/services/priceMasterApi'
import { ItemType, PriceMaster } from '@/types/system-setting/priceMaster'
import Combobox from '@/components/ui/common/combobox'
import { Eye } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import PriceMasterForm from "../../price_master/modal/form"

interface BookingAddOnProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  setValue: UseFormSetValue<FacilitySchedule>;
  trigger?: UseFormTrigger<FacilitySchedule>;
  action: 'add' | 'edit';
  open: boolean;
}

const { VITE_VIEW_S3_URL } = import.meta.env;

const BookingAddOn = ({ control, errors, watch, setValue, trigger, action, open }: BookingAddOnProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const facilityId = watch("general_info.facility_id");
  const scheduleAddOns = watch("add_ons") || [];

  // Get facility details with add-ons
  const { data: facilityDetails, isLoading } = useGetFacilityByIdQuery({ id: facilityId, org_id: orgId, query: "load_add_ons=true" }, {
    skip: !facilityId || action === 'edit'
  });

  // Fetch all facilities that can be used as add-ons to source their images and names
  const { data: addOnFacilities, isLoading: isLoadingAddOnFacilities } = useGetFacilitiesQuery({
    query: "is_add_on=true",
    orgId,
  });

  const { data: productData, isLoading: isLoadingProducts } = useGetAllProductsQuery({
    query: "status=active&sort_key=id&sort_order=desc",
    org_id: orgId,
  });

  // Lightbox state for viewing add-on facility images as a gallery
  const [gallery, setGallery] = useState<{ open: boolean; images: string[]; index: number }>(
    { open: false, images: [], index: 0 }
  );

  const availableAddOns = useMemo(() => {
    if (action === 'edit') {
      return scheduleAddOns;
    }
    return facilityDetails?.add_ons || [];
  }, [facilityDetails, action, scheduleAddOns]);

  // Fetch Price Masters for facilities and products
  const { data: facilityPriceMasters } = useGetPriceMastersQuery(
    { query: "item_type=facility_slot&status=active", org_id: orgId },
    { skip: !orgId || !open }
  );

  const { data: productPriceMasters } = useGetPriceMastersQuery(
    { query: "item_type=product&status=active", org_id: orgId },
    { skip: !orgId || !open }
  );

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedRuleData, setSelectedRuleData] = useState<PriceMaster | null>(null);

  const facilityPriceMasterOptions = useMemo(() => {
    return facilityPriceMasters && Array.isArray((facilityPriceMasters as any).data)
      ? (facilityPriceMasters as any).data.map((rule: any) => ({
        label: `${rule.rule_code} : ${rule.rule_name}`,
        subLabel: rule.rule_name,
        value: String(rule.id),
      }))
      : [];
  }, [facilityPriceMasters]);

  const productPriceMasterOptions = useMemo(() => {
    return productPriceMasters && Array.isArray((productPriceMasters as any).data)
      ? (productPriceMasters as any).data.map((rule: any) => ({
        label: `${rule.rule_code} : ${rule.rule_name}`,
        subLabel: rule.rule_name,
        value: String(rule.id),
      }))
      : [];
  }, [productPriceMasters]);

  const handlePreview = (ruleId: number, type: 'facility' | 'product') => {
    const data = type === 'facility' ? facilityPriceMasters : productPriceMasters;
    const rule = (data as any)?.data?.find((r: any) => r.id === ruleId);
    if (rule) {
      setSelectedRuleData(rule);
      setPreviewOpen(true);
    }
  };

  // Track which facility we've initialized add-ons for
  const initializedFacilityRef = useRef<number | null>(null);
  const resolvePriceMasterId = (addOn: any): number | undefined => {
    if (addOn.price_master_id) return addOn.price_master_id;
    if (addOn.item_type === "product") {
      return productData?.data?.find((p: any) => p.id === addOn.item_id)?.price_master_id ?? undefined;
    }
    if (addOn.item_type === "facility") {
      return addOnFacilities?.data?.find((f: any) => f.id === addOn.item_id)?.price_master_id ?? undefined;
    }
    return undefined;
  };

  // Initialize add_on_ids with inclusive add-ons when facility changes AND no add-ons are currently selected
  useEffect(() => {
    if (action !== 'add') return;
    if (facilityId && initializedFacilityRef.current !== facilityId && scheduleAddOns.length === 0) {
      if (availableAddOns.length > 0) {
        const initialScheduleAddOns = availableAddOns
          .filter(addOn => addOn.type === FacilityAddOnType.Inclusive)
          .map(addOn => {
            return {
              id: addOn.id!,
              max_attendees: addOn.item_type == "facility" ? 1 : 0,
              price: addOn.price || 0,
              name: addOn.name || "",
              type: addOn.type,
              item_id: addOn.item_id!,
              item_type: addOn.item_type || "facility",
              quantity: 1,
              price_master_id: resolvePriceMasterId(addOn)
            };
          });
        console.log("Auto-selecting inclusive add-ons for new facility:", initialScheduleAddOns);
        setValue("add_ons", initialScheduleAddOns);
        // Mark as initialized only after add-ons have been loaded and applied
        initializedFacilityRef.current = facilityId;
      } else {
        // Mark initialized even if none available to avoid repeated attempts on re-renders
        console.log("No add-ons available for facility; initialized with empty selection");
        initializedFacilityRef.current = facilityId;
      }
    }
  }, [availableAddOns, facilityId, setValue, scheduleAddOns.length, addOnFacilities?.data, productData?.data]);

  // Ensure inclusive add-ons are always present in form (even when editing existing schedules)
  useEffect(() => {
    if (action !== 'add') return;
    if (!facilityId || availableAddOns.length === 0) return;
    const inclusiveFromApi = availableAddOns.filter(a => a.type === FacilityAddOnType.Inclusive);
    if (inclusiveFromApi.length === 0) return;

    const missing = inclusiveFromApi.filter(apiAddOn =>
      !scheduleAddOns.some((formAddOn: { id: number | undefined; add_on_facility_id?: number }) =>
        formAddOn.id === apiAddOn.id || formAddOn.add_on_facility_id === apiAddOn.item_id
      )
    );

    if (missing.length > 0) {
      const toAdd = missing.map(addOn => {
        return {
          id: addOn.id!,
          max_attendees: addOn.item_type == "facility" ? 1 : 0,
          price: addOn.price || 0,
          name: addOn.name || "",
          type: addOn.type,
          item_id: addOn.item_id!,
          item_type: addOn.item_type || "facility",
          price_master_id: resolvePriceMasterId(addOn)
        };
      });
      const merged = [...scheduleAddOns, ...toAdd];
      console.log("Merging missing inclusive add-ons into form:", { missing: toAdd, previous: scheduleAddOns, merged });
      setValue("add_ons", merged, { shouldDirty: true, shouldValidate: true });
      if (trigger) trigger("add_ons");
    }
  }, [facilityId, availableAddOns, scheduleAddOns, addOnFacilities?.data, setValue, trigger]);

  const handleRemoveAddOn = (addOnIdToRemove: number | undefined) => {
    console.log("Removing add-on:", { addOnIdToRemove, currentSelectedAddOns: scheduleAddOns });
    const updatedScheduleAddOns = scheduleAddOns.filter((addOn: { id: number | undefined; }) => addOn.id !== addOnIdToRemove);
    console.log("Updated schedule add-ons after removal:", updatedScheduleAddOns);
    setValue("add_ons", updatedScheduleAddOns);
    if (trigger) {
      trigger("add_ons");
    }
  };

  const isAddOnSelected = (addOnId: number | undefined, itemId?: number) => {
    const isSelected = scheduleAddOns.some((a: { id: number | undefined; item_id?: number }) => (
      a.id === addOnId || (itemId ? a.item_id === itemId : false)
    ));
    console.log("Checking if add-on is selected:", { addOnId, itemId, scheduleAddOns, isSelected });
    return isSelected;
  };

  if (isLoading || isLoadingAddOnFacilities || isLoadingProducts) {
    return (
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading add-ons...</div>
          </div>
        </Card>
      </div>
    );
  }

  console.log({ scheduleAddOns, addOnFacilities, facilityDetails, availableAddOns });


  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
          <p className="font-medium text-base">Add Ons</p>
          <div className="text-sm text-muted-foreground">
            {availableAddOns.length} add-on(s) linked to this facility
          </div>
        </div>
        <Separator className="h-[1px] my-4" />

        {availableAddOns.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-muted-foreground mb-2">
              <i className="fa fa-info-circle text-4xl text-gray-300"></i>
            </div>
            <p className="text-sm text-muted-foreground">
              No add-ons linked with the facility selected
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              You can still proceed to the next step
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {availableAddOns.map((addOn, index) => {
              const selected = action === 'edit' ? true : isAddOnSelected(addOn.id!, addOn.item_id);
              const canRemove = addOn.type === FacilityAddOnType.Exclusive;
              const selectedIndex = scheduleAddOns.findIndex((item: { id: number | undefined; item_id?: number }) => (
                item.id === addOn.id || item.item_id === addOn.item_id
              ));
              return (
                <Card
                  key={addOn.item_id}
                  className={cn(
                    "transition-all duration-200",
                    selected
                      ? "border-primary/50 bg-primary/5"
                      : "border-gray-200 bg-gray-50 opacity-70"
                  )}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-3 h-3 rounded-full",
                          selected ? "bg-primary" : "bg-gray-300"
                        )}></div>
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">
                            {addOn.name}
                          </span>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <div className={cn(
                              "inline-flex w-fit px-2 py-0.5 rounded text-[11px]",
                              addOn.type === FacilityAddOnType.Inclusive
                                ? "bg-green-100 text-green-800 border border-green-200"
                                : "hidden"
                            )}>
                              {addOn.type === FacilityAddOnType.Inclusive ? "Included" : ""}
                            </div>
                            <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 text-[11px] px-2 py-0">
                              {formatToPKR(addOn.price || 0)}
                            </Badge>
                            {(() => {
                              const pmId = resolvePriceMasterId(addOn);
                              const pm = (addOn.item_type === "product" ? productPriceMasters : facilityPriceMasters)?.data?.find((p: any) => p.id === pmId);
                              if (!pm) return null;
                              return (
                                <>
                                  {pm.enable_special_pricing && (
                                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 h-4">
                                      Special
                                    </Badge>
                                  )}
                                  {pm.enable_plan_pricing && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] px-1.5 py-0 h-4">
                                      Plan
                                    </Badge>
                                  )}
                                  {pm.enable_group_pricing && (
                                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0 h-4">
                                      Group
                                    </Badge>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      {canRemove && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Include</span>
                          <button
                            type="button"
                            onClick={() => {
                              console.log("Toggle clicked:", {
                                addOnId: addOn.id,
                                isSelected: selected,
                                currentSelectedIds: scheduleAddOns
                              });
                              if (selected) {
                                handleRemoveAddOn(addOn.id!);
                              } else {
                                const newScheduleAddOns = [...scheduleAddOns, {
                                  id: addOn.id!,
                                  max_attendees: addOn.item_type == "facility" ? 1 : 0,
                                  price: addOn.price || 0,
                                  name: addOn.name || "",
                                  type: addOn.type,
                                  item_id: addOn.item_id!,
                                  item_type: addOn.item_type || "facility",
                                  price_master_id: resolvePriceMasterId(addOn)
                                }];
                                console.log("Setting new schedule add-ons:", newScheduleAddOns);
                                setValue("add_ons", newScheduleAddOns);
                                if (trigger) {
                                  trigger("add_ons");
                                }
                              }
                            }}
                            className={cn(
                              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                              selected ? "bg-primary" : "bg-gray-300"
                            )}
                            aria-pressed={selected}
                            aria-label="Toggle include add-on"
                          >
                            <span
                              className={cn(
                                "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
                                selected ? "translate-x-5" : "translate-x-1"
                              )}
                            />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {/* Item Name (read-only) */}
                      <div className="space-y-1">
                        <Label className="text-sm font-medium">Item Name</Label>
                        <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                          {addOn.name}
                        </div>
                        <span className="text-xs text-muted-foreground">Non-editable</span>
                      </div>

                      {/* Item Type */}
                      <div className="space-y-1">
                        <Label className="text-sm font-medium">Item Type</Label>
                        <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700 capitalize">
                          {addOn.item_type}
                        </div>
                        <span className="text-xs text-muted-foreground">Non-editable</span>
                      </div>

                      {/* Price Rule */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">Price Rule {selected && <span className="text-red-500">*</span>}</Label>
                          {watch(`add_ons.${selectedIndex}.price_master_id`) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              className="h-6 px-2 text-primary hover:text-primary/80"
                              onClick={() => handlePreview(
                                Number(watch(`add_ons.${selectedIndex}.price_master_id`)),
                                addOn.item_type === 'product' ? 'product' : 'facility'
                              )}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </Button>
                          )}
                        </div>
                        <Controller
                          name={`add_ons.${selectedIndex}.price_master_id`}
                          control={control}
                          rules={{ required: selected ? "Price Rule is required" : false }}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <div className="space-y-1">
                              <Combobox
                                list={addOn.item_type === 'product' ? productPriceMasterOptions : facilityPriceMasterOptions}
                                defaultValue={
                                  (addOn.item_type === 'product' ? productPriceMasterOptions : facilityPriceMasterOptions)
                                    ?.find((option: any) => option.value === value?.toString())?.label || ""
                                }
                                setFilter={(selectedLabel: string) => {
                                  const options = addOn.item_type === 'product' ? productPriceMasterOptions : facilityPriceMasterOptions;
                                  const selectedOption = options.find(
                                    (option: any) => option.label === selectedLabel
                                  );
                                  onChange(selectedOption ? Number(selectedOption.value) : undefined);
                                }}
                                label="Price Rule"
                                showLabel={false}
                                disabled={!selected || action === 'edit'}
                              />
                              {error && (
                                <span className="text-red-500 text-xs">{error.message}</span>
                              )}
                              {action === 'edit' && (
                                <span className="text-xs text-muted-foreground">Non-editable</span>
                              )}
                            </div>
                          )}
                        />
                      </div>

                      {/* Max Attendees - Only if facility has check-in */}
                      {(action === 'edit' || (facilityDetails?.bookable && (addOn?.access_control || addOn.item_type === "product"))) && selected && (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">
                              {addOn.item_type === "product" ? "Quantity" : "Max Attendees"} <span className="text-red-500">*</span>
                            </Label>
                            {addOn.item_type === "product" && (
                              <span className="text-[11px] font-medium text-amber-600">
                                Available: {productData?.data?.find(p => p.id === addOn.item_id)?.quantity || 0}
                              </span>
                            )}
                          </div>
                          <Controller
                            name={`add_ons.${selectedIndex}.max_attendees`}
                            control={control}
                            defaultValue={1}
                            rules={{
                              required: "This field is required",
                              validate: (value) => {
                                const numValue = Number(value);
                                if (numValue < 1) return "Value must be at least 1";

                                if (addOn.item_type === "product") {
                                  const product = productData?.data?.find(p => p.id === addOn.item_id);
                                  if (product && product.manage_inventory) {
                                    const availableStock = product.quantity || 0;
                                    if (numValue > availableStock) {
                                      return `Quantity cannot exceed available stock of ${availableStock} units.`;
                                    }
                                  }
                                } else {
                                  const addOnFacility = addOnFacilities?.data?.find(f => f.id === addOn.item_id);
                                  if (addOnFacility?.max_capacity && numValue > addOnFacility.max_capacity) {
                                    return `Max Attendees cannot exceed the facility capacity of ${addOnFacility.max_capacity}.`;
                                  }
                                }
                                return true;
                              }
                            }}
                            render={({ field }) => (
                              <Input
                                type="number"
                                min={1}
                                placeholder={addOn.item_type === "product" ? "Enter quantity" : "Enter max attendees"}
                                disabled={!selected || action === 'edit'}
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value === "" ? "" : Number(e.target.value))}
                                className={cn(
                                  (!selected || action === 'edit') && "bg-gray-100 text-gray-500",
                                  errors?.add_ons?.[selectedIndex]?.max_attendees && "border-red-500 focus-visible:ring-red-500"
                                )}
                              />
                            )}
                          />
                          {errors?.add_ons?.[selectedIndex]?.max_attendees && (
                            <p className="text-red-500 text-[11px] mt-0.5 font-medium">
                              {errors.add_ons?.[selectedIndex]?.max_attendees?.message}
                            </p>
                          )}
                          {action === 'edit' && (
                            <span className="text-xs text-muted-foreground">Non-editable</span>
                          )}
                        </div>
                      )}

                      {/* Type */}
                      <div className="space-y-1">
                        <Label className="text-sm font-medium">Type</Label>
                        <div className={cn(
                          "p-2 rounded border text-sm font-medium",
                          addOn.type === FacilityAddOnType.Inclusive
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-blue-100 text-blue-800 border-blue-200"
                        )}>
                          {addOn.type === FacilityAddOnType.Inclusive ? "Inclusive" : "Exclusive"}
                        </div>
                        <span className="text-xs text-muted-foreground">Non-editable</span>
                      </div>

                      {/* Description */}
                      {addOn.description && (
                        <div className="space-y-1 col-span-full">
                          <Label className="text-sm font-medium">Description</Label>
                          <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                            {addOn.description}
                          </div>
                        </div>
                      )}

                      {/* Images - sourced from the selected add-on facility or product */}
                      {(() => {
                        const images: string[] = addOn.item_type === "facility"
                          ? (Array.isArray(addOnFacilities?.data?.find((f) => f.id === addOn.item_id)?.image_url)
                            ? (addOnFacilities?.data?.find((f) => f.id === addOn.item_id)?.image_url as string[])
                            : [])
                          : (productData?.data?.find((p) => p.id === addOn.item_id)?.image_url
                            ? [productData?.data?.find((p) => p.id === addOn.item_id)?.image_url as string]
                            : []);
                        const openLightbox = (startIndex: number) => {
                          setGallery({ open: true, images, index: startIndex });
                        };
                        return (
                          <div className="space-y-1 col-span-full">
                            <div className="flex items-center justify-between">
                              <Label className="text-sm font-medium">Images</Label>
                              <div className="flex items-center gap-3">
                                <span className="text-[11px] text-gray-500">{images.length} {images.length === 1 ? "image" : "images"}</span>
                                {images.length > 0 && (
                                  <Button type="button" variant="outline" size="sm" onClick={() => openLightbox(0)}>
                                    View Gallery
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 h-[96px] w-full border bg-background rounded-md bg-gray-100 overflow-x-auto px-2">
                              {isLoadingAddOnFacilities ? (
                                <>
                                  {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="h-[72px] w-[72px] rounded bg-gray-200 animate-pulse" />
                                  ))}
                                </>
                              ) : images.length > 0 ? (
                                (() => {
                                  const preview = images.slice(0, 5);
                                  const remaining = images.length - preview.length;
                                  return (
                                    <>
                                      {preview.map((imgUrl, i) => (
                                        <button
                                          key={i}
                                          type="button"
                                          onClick={() => openLightbox(i)}
                                          className="relative h-[72px] w-[72px] shrink-0 rounded overflow-hidden border hover:ring-2 hover:ring-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/80"
                                          aria-label={`View image ${i + 1}`}
                                        >
                                          <img
                                            src={`${VITE_VIEW_S3_URL}/${imgUrl}`}
                                            alt={`Add-on ${i + 1}`}
                                            className="h-full w-full object-cover"
                                          />
                                          {i === preview.length - 1 && remaining > 0 && (
                                            <div className="absolute inset-0 bg-black/50 text-white flex items-center justify-center text-sm">
                                              +{remaining}
                                            </div>
                                          )}
                                        </button>
                                      ))}
                                    </>
                                  );
                                })()
                              ) : (
                                <div className="flex items-center justify-center w-full">
                                  <p className="text-xs text-gray-500">No images for selected add-on facility</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </Card>

      {gallery.open && (
        <div className="fixed inset-0 z-[60] top-0">
          <div
            className="absolute inset-0 -top-4 bg-black/70"
            onClick={() => setGallery({ open: false, images: [], index: 0 })}
          />
          <div className="absolute inset-0 flex items-center justify-center ">
            <div className="relative max-w-[90vw] min-h-[45vh] max-h-[85vh] w-full flex items-center justify-center ">
              <button
                type="button"
                className="absolute top-2 right-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                aria-label="Close gallery"
                onClick={() => setGallery({ open: false, images: [], index: 0 })}
              >
                <i className="fa fa-xmark" />
              </button>

              {gallery.images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="absolute left-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                    aria-label="Previous image"
                    onClick={() =>
                      setGallery((g) => ({
                        ...g,
                        index: (g.index - 1 + g.images.length) % g.images.length,
                      }))
                    }
                  >
                    <i className="fa fa-chevron-left" />
                  </button>
                  <button
                    type="button"
                    className="absolute right-2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                    aria-label="Next image"
                    onClick={() =>
                      setGallery((g) => ({
                        ...g,
                        index: (g.index + 1) % g.images.length,
                      }))
                    }
                  >
                    <i className="fa fa-chevron-right" />
                  </button>
                </>
              )}

              <img
                src={`${VITE_VIEW_S3_URL}/${gallery.images[gallery.index]}`}
                alt={`Gallery image ${gallery.index + 1}`}
                className="max-h-[85vh] max-w-[90vw] object-contain rounded shadow-lg"
              />
              {gallery.images.length > 0 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs text-white bg-black/50 rounded px-2 py-0.5">
                  {gallery.index + 1} / {gallery.images.length}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="overflow-y-auto sm:max-w-[70vw] p-0" side="right">
          <SheetHeader className="p-6 border-b">
            <SheetTitle>Price master Detail</SheetTitle>
          </SheetHeader>
          <div className="p-6">
            {selectedRuleData && (
              <PriceMasterForm
                isOpen={previewOpen}
                setOpen={setPreviewOpen}
                action="edit"
                data={selectedRuleData}
                readOnly={true}
                setData={() => {}}
                setAction={() => {}}
                refetch={() => {}}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BookingAddOn;